<template>
	<div class="protocol-list-container">
		<!-- 搜索区域 -->
		<el-card class="search-card" shadow="never">
			<!-- 页面标题 -->
			<div class="page-title">
				下载中心
			</div>

			<div class="search-form">
				<!-- 第二行查询条件 -->
				<div class="search-row">
					<div class="search-item">
						<label class="search-label">时间范围</label>
						<div class="search-control date-range-control">
							<el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至"
								start-placeholder="开始日期" end-placeholder="结束日期" value-format="timestamp"
								:clearable="true" />
						</div>
					</div>

					<div class="search-actions">
						<el-button class="search" type="primary" icon="el-icon-search" @click="handleSearch" :loading="loading">
							搜索
						</el-button>
						<el-button class="reset" icon="el-icon-refresh" @click="handleReset">重置</el-button>
					</div>
				</div>
			</div>
		</el-card>

		<!-- 数据表格区域 -->
		<div class="table-area">
			<el-table :data="tableData" style="width: 100%" v-loading="loading" element-loading-text="数据加载中..."
				element-loading-background="rgba(255, 255, 255, 0.7)" size="small" height="calc(100vh - 340px)"
				@selection-change="handleSelectionChange" :row-key="getRowKey">
				<el-table-column type="selection" width="45" align="center" fixed="left" />
				<el-table-column prop="id" label="编号" width="100" align="center" fixed="left" />
				<el-table-column prop="fileName" label="文件名称" min-width="200" show-overflow-tooltip>
					<template #default="{ row }">
						<div class="file-name-cell">
							<i v-if="getFileIcon(row.fileSuffix)" :class="getFileIcon(row.fileSuffix)" class="file-icon"></i>
							<span class="file-name-text">{{ row.fileName }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column prop="fileDataSize" label="文件大小" width="120" align="center">
					<template #default="{ row }">
						{{ formatFileSize(row.fileDataSize) }}
					</template>
				</el-table-column>
				<el-table-column prop="fileSuffix" label="文件类型" width="120" align="center" />
				<el-table-column prop="status" label="状态" width="120" align="center">
					<template #default="{ row }">
						<el-tag :type="getStatusType(row.status)" size="small" effect="plain">
							{{ getStatusText(row.status) }}
						</el-tag>
					</template>
				</el-table-column>
				<el-table-column prop="reason" label="原因" width="150" show-overflow-tooltip align="center" />
				<el-table-column prop="gmtCreate" label="创建时间" width="180" align="center">
					<template #default="{ row }">
						{{ formatDateTime(row.gmtCreate) }}
					</template>
				</el-table-column>
				<el-table-column label="操作" width="180" align="center" fixed="right">
					<template #default="{ row }">
						<div class="operation-buttons">
							<el-button type="primary" size="small" @click="handleDownload(row)" :loading="row.downloading"
								:disabled="row.status !== 1" icon="el-icon-download">
								{{ row.downloading ? '下载中' : '下载' }}
							</el-button>
						</div>
					</template>
				</el-table-column>
			</el-table>

			<!-- 分页 -->
			<div class="pagination-area">
				<el-pagination v-model:current-page="pagination.currentPage" v-model:page-size="pagination.pageSize"
					:page-sizes="[10, 20, 50, 100]" :total="pagination.total"
					layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
					@current-change="handleCurrentChange" size="small" />
			</div>
		</div>

	</div>
</template>

<script>
	export default {
		name: 'DownloadCenter',
		data() {
			return {
				// 搜索表单数据
				searchForm: {
					dateRange: [],
				},

				// 表格数据
				tableData: [],

				// 选中的行
				selectedRows: [],

				// 分页数据
				pagination: {
					currentPage: 1,
					pageSize: 10,
					total: 0
				},

			}
		},
		methods: {
			// 搜索
			handleSearch() {
				this.pagination.currentPage = 1;
				this.fetchTableData();
			},

			// 重置搜索
			handleReset() {
				this.searchForm = {
					dateRange: [],
					fileName: '',
					status: ''
				};
				this.handleSearch();
			},

			// 刷新表格
			refreshTable() {
				this.fetchTableData();
			},

			// 下载文件
			async handleDownload(row) {
				if (row.status !== 1) {
					this.$message.warning('只有生成成功的文件才能下载');
					return;
				}

				// 设置下载中状态
				this.$set(row, 'downloading', true);

				try {
					// 调用下载接口 GET file-info-do/export-to-excel/{id}
					const res = await this.$download(
						`file-info-do/export-to-excel/${row.id}`);

					// 下载成功后更新文件大小（如果需要）
					if (res && res.data) {
						// 可以在这里处理下载后的逻辑
						this.$message.success('文件下载成功');
					}
				} catch (error) {
					console.error('下载文件失败:', error);
					this.$message.error(error.message || '文件下载失败');
				} finally {
					// 重置下载状态
					this.$set(row, 'downloading', false);
				}
			},

			// 表格选择变化
			handleSelectionChange(selection) {
				this.selectedRows = selection;
			},

			// 获取行唯一标识
			getRowKey(row) {
				return row.id;
			},

			// 分页大小变化
			handleSizeChange(size) {
				this.pagination.pageSize = size;
				this.fetchTableData();
			},

			// 当前页变化
			handleCurrentChange(page) {
				this.pagination.currentPage = page;
				this.fetchTableData();
			},

			// 获取表格数据
			async fetchTableData() {
				this.loading = true;

				try {
					// 构建查询参数
					const params = this.buildQueryParams();

					// 调用后端接口
					const res = await this.$etcGet('file-info-do/get-file-info-page', params);

					if (res.code === 0 && res.result) {
						this.processTableData(res.result);
					} else {
						this.resetTableData();
						this.$message.warning(res.message || '未查询到数据');
					}
				} catch (error) {
					this.handleFetchError(error);
				} finally {
					this.loading = false;
				}
			},

			// 构建查询参数
			buildQueryParams() {
				const params = {
					pageIndex: this.pagination.currentPage,
					pageSize: this.pagination.pageSize
				};

				// 添加时间范围条件
				if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
					params.beginTime = this.searchForm.dateRange[0];
					params.endTime = this.searchForm.dateRange[1];
				}

				// 添加文件名称条件
				if (this.searchForm.fileName) {
					params.fileName = this.searchForm.fileName;
				}

				// 添加状态条件
				if (this.searchForm.status !== '') {
					params.status = this.searchForm.status;
				}

				return params;
			},

			// 处理表格数据
			processTableData(result) {
				this.tableData = (result.list || result.records || []).map(item => ({
					...item,
					downloading: false,
					gmtCreate: this.formatDateTime(item.gmtCreate),
					gmtModified: this.formatDateTime(item.gmtModified)
				}));

				this.pagination.total = result.total || result.totalCount || 0;
			},

			// 重置表格数据
			resetTableData() {
				this.tableData = [];
				this.pagination.total = 0;
			},

			// 处理获取数据错误
			handleFetchError(error) {
				const errorMessage = error.message || '获取文件列表失败';
				this.$notify.error({
					title: '数据获取失败',
					message: errorMessage
				});
			},

			// 获取状态标签类型
			getStatusType(status) {
				const typeMap = {
					1: 'success', // 成功
					0: 'danger', // 失败
					2: 'warning' // 处理中
				};
				return typeMap[status] || 'info';
			},

			// 获取状态文本
			getStatusText(status) {
				const textMap = {
					1: '生成成功',
					0: '生成失败',
					2: '处理中'
				};
				return textMap[status] || '未知状态';
			},

			// 格式化文件大小
			formatFileSize(size) {
				if (size === null || size === undefined || size === 0) return '-';

				const units = ['B', 'KB', 'MB', 'GB', 'TB'];
				let index = 0;
				let fileSize = Number(size);

				while (fileSize >= 1024 && index < units.length - 1) {
					fileSize /= 1024;
					index++;
				}

				return `${fileSize.toFixed(2)} ${units[index]}`;
			},

			// 格式化日期时间
			formatDateTime(dateTime) {
				if (!dateTime) return '-';
				try {
					// 如果已经是格式化好的字符串，直接返回
					if (typeof dateTime === 'string') {
						// 尝试解析和重新格式化
						const date = new Date(dateTime);
						if (isNaN(date.getTime())) {
							return dateTime; // 如果不是有效日期，返回原字符串
						}
						// 格式化日期时间
						const year = date.getFullYear();
						const month = String(date.getMonth() + 1).padStart(2, '0');
						const day = String(date.getDate()).padStart(2, '0');
						const hours = String(date.getHours()).padStart(2, '0');
						const minutes = String(date.getMinutes()).padStart(2, '0');
						const seconds = String(date.getSeconds()).padStart(2, '0');

						return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
					}
					// 如果是时间戳
					if (typeof dateTime === 'number') {
						const date = new Date(dateTime);
						if (isNaN(date.getTime())) return '-';

						const year = date.getFullYear();
						const month = String(date.getMonth() + 1).padStart(2, '0');
						const day = String(date.getDate()).padStart(2, '0');
						const hours = String(date.getHours()).padStart(2, '0');
						const minutes = String(date.getMinutes()).padStart(2, '0');
						const seconds = String(date.getSeconds()).padStart(2, '0');

						return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
					}
					return '-';
				} catch (error) {
					console.error('格式化日期时间错误:', error);
					return '-';
				}
			},

			// 获取文件图标（使用 Element UI 的图标类）
			getFileIcon(fileSuffix) {
				if (!fileSuffix) return 'el-icon-document';

				const iconMap = {
					'.xlsx': 'el-icon-document-copy',
					'.xls': 'el-icon-document-copy',
					'.csv': 'el-icon-document-copy',
					'.pdf': 'el-icon-document',
					'.doc': 'el-icon-document',
					'.docx': 'el-icon-document',
					'.txt': 'el-icon-document',
					'.jpg': 'el-icon-picture-outline',
					'.jpeg': 'el-icon-picture-outline',
					'.png': 'el-icon-picture-outline',
					'.gif': 'el-icon-picture-outline',
					'.mp4': 'el-icon-video-play',
					'.avi': 'el-icon-video-play',
					'.mp3': 'el-icon-headset',
					'.wav': 'el-icon-headset',
					'.zip': 'el-icon-folder-opened',
					'.rar': 'el-icon-folder-opened',
					'.7z': 'el-icon-folder-opened'
				};

				// 检查后缀（带点和不带点）
				const suffixWithDot = fileSuffix.startsWith('.') ? fileSuffix : `.${fileSuffix}`;
				return iconMap[suffixWithDot.toLowerCase()] || 'el-icon-document';
			}
		},
		mounted() {
			this.fetchTableData();
		}
	}
</script>

<style scoped lang="scss">
	.protocol-list-container {
		padding: 20px;
		background-color: #f5f7fa;
		height: 100%;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.search-card {
		margin-bottom: 16px;
		border-radius: 10px;

		:deep(.el-card__body) {
			padding: 24px 20px !important;
		}
	}

	.page-title {
		margin-bottom: 20px;
		font-size: 20px;
		color: #3b3c3f;
	}

	.search-form {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.search-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 12px;
	}

	.search-item {
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 280px;
		max-width: 280px;
	}

	.search-label {
		width: 70px;
		flex-shrink: 0;
		text-align: left;
		padding-right: 8px;
		font-size: 14px;
		color: #707070;
		line-height: 32px;
	}

	.search-control {
		flex: 1;
		min-width: 0;

		:deep(.el-input),
		:deep(.el-select) {
			width: calc(100% - 20px);

			.el-input__inner {
				height: 32px;
				line-height: 32px;
				font-size: 14px;
			}
		}

		&.date-range-control {
			:deep(.el-date-picker) {
				width: calc(100% - 20px);

				.el-range-editor {
					height: 32px;
					padding: 0 8px;
					width: 100%;
					box-sizing: border-box;

					.el-range-input {
						font-size: 13px;
						width: 45%;
					}

					.el-range-separator {
						line-height: 24px;
						font-size: 12px;
						padding: 0 4px;
						width: 10%;
						text-align: center;
					}
				}
			}
		}
	}

	.search-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		height: 32px;
		flex-shrink: 0;
		margin-left: auto;

		.el-button {
			border-radius: 5px;
			width: 70px;
		}

		.search {
			background-color: #2280fd;
			border-color: #2280fd;
		}

		.reset {
			background-color: #fff;
			border-color: #e7e7e7;
		}
	}

	.table-area {
		flex: 1;
		display: flex;
		flex-direction: column;
		background: #fff;
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
		padding: 20px;

		/* 表格容器 */
		:deep(.el-table) {
			flex: 1;
			min-height: 0;
			border-bottom: 1px solid #ebeef5;

			/* 让表格内部产生滚动 */
			.el-table__body-wrapper {
				overflow-y: auto;
			}
		}

		/* 分页区域 */
		.pagination-area {
			flex-shrink: 0;
			background: #fff;
			border-radius: 6px;
			padding: 12px 16px;
			margin-top: 16px;
			display: flex;
			justify-content: flex-end;
		}
	}

	// 文件名称单元格样式
	.file-name-cell {
		display: flex;
		align-items: center;
		gap: 8px;

		.file-icon {
			color: #409eff;
			font-size: 16px;
			flex-shrink: 0;
		}

		.file-name-text {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	// 操作按钮容器
	.operation-buttons {
		display: flex;
		align-items: center;
		gap: 8px;
		justify-content: center;
	}

	// 删除确认对话框样式
	.delete-dialog-content {
		text-align: center;
		padding: 20px 0;

		p {
			margin: 15px 0;
			font-size: 16px;
			color: #303133;
		}

		.delete-tip {
			font-size: 14px;
			color: #f56c6c;
		}
	}

	// 紧凑的表格样式
	:deep(.el-table) {
		font-size: 14px;

		&.el-table--small {
			.el-table__header {
				th {
					padding: 6px 0;
					background-color: #f8fafc;
					color: #707070;
					height: 36px;
				}
			}

			.el-table__body {
				td {
					padding: 4px 0;
					height: 36px;
				}

				tr:hover>td {
					background-color: #f5f7fa !important;
				}
			}

			.el-button--small {
				padding: 5px 8px;
				font-size: 11px;
			}
		}

		.el-tag {
			font-size: 11px;
			height: 22px;
			line-height: 20px;
			padding: 0 6px;
		}
	}

	// 响应式调整
	@media (max-width: 1600px) {
		.search-item {
			min-width: 150px;
			max-width: 180px;
		}
	}

	@media (max-width: 1440px) {
		.search-item {
			min-width: 140px;
			max-width: 160px;
		}

		.search-label {
			width: 65px;
			padding-right: 6px;
			font-size: 13px;
		}

		.search-control {
			&.date-range-control {
				:deep(.el-date-picker) {
					width: calc(100% - 15px);

					.el-range-editor {
						padding: 0 6px;

						.el-range-input {
							font-size: 12px;
							max-width: 60px;
						}

						.el-range-separator {
							font-size: 11px;
							padding: 0 3px;
						}
					}
				}
			}
		}
	}

	@media (max-width: 1200px) {
		.search-form {
			gap: 8px;
		}

		.search-row {
			gap: 8px;

			&:last-child {
				flex-wrap: nowrap;
				overflow-x: auto;
			}
		}

		.search-item {
			min-width: calc(20% - 8px);
			max-width: calc(20% - 8px);
			flex-shrink: 1;
		}

		.search-actions {
			margin-left: 0;
			flex-shrink: 0;

			:deep(.el-button) {
				white-space: nowrap;
			}
		}

		.search-control {
			&.date-range-control {
				:deep(.el-date-picker) {
					width: calc(100% - 10px);

					.el-range-editor {
						.el-range-input {
							width: 40%;
						}
					}
				}
			}
		}
	}

	@media (max-width: 992px) {
		.search-item {
			min-width: calc(33.333% - 8px);
			max-width: calc(33.333% - 8px);
		}

		.search-row:last-child {
			flex-wrap: wrap;

			.search-actions {
				width: 100%;
				margin-top: 8px;
				justify-content: flex-start;
			}
		}

		.operation-left {
			flex-wrap: wrap;
			gap: 8px;

			.el-button {
				flex: 1;
				min-width: calc(50% - 8px);
			}

			.selection-tips {
				width: 100%;
				margin-top: 8px;
				margin-left: 0;
				text-align: center;
			}
		}
	}

	@media (max-width: 768px) {
		.search-item {
			min-width: 100%;
			max-width: 100%;
		}

		.search-actions {
			justify-content: center;

			:deep(.el-button) {
				width: 100px;
			}
		}

		.operation-buttons {
			flex-direction: column;
			gap: 4px;

			.el-button {
				width: 100%;
			}
		}
	}
</style>